import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, needsIntro, needsList, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-[#F8F3EC]" data-id="element-183" data-mp="1xwgc6udciflt">
      <div className="max-w-4xl mx-auto" data-id="element-184" data-mp="21zlaslub1x56">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-185" data-mp="220o22uvji1lm">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#520128] uppercase" data-id="element-186" data-mp="1o0d4za3mzqs0">
            Still Spending Hours Preparing{' '}
            <span className="text-[#8E2C5C]" data-id="element-187" data-mp="1t0hq5dfhp9ok">Your Sewing Classes</span>?
          </h2>
          <div className="text-lg text-slate-700 leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-[#E6D5BF] text-left space-y-4" data-id="element-188" data-mp="3iee8w9e1n3yy">
            <p data-id="element-189" data-mp="1z057l1p5eoy1">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-190" data-mp="1yyc23pq9uarm">
                  {line}
                  <br data-id="element-191" data-mp="1u12dudemc55d" />
                </React.Fragment>)}
            </p>
            <p className="text-[#520128] font-semibold" data-id="element-192" data-mp="27rp2tdukugo9">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-193" data-mp="23rx2fofc7gtz" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-194" data-mp="1g2cgcwnw03gh" />}
                </React.Fragment>)}
            </p>
            <p className="text-[#520128] font-semibold" data-id="element-195" data-mp="1b416wn1i7uys">{needsIntro}</p>
            <p data-id="element-196" data-mp="1p30ml571gzit">
              {needsList.map((line, index) => <React.Fragment key={line} data-id="element-197" data-mp="1p04nhdjgg4ss" data-mpi={index}>
                  {line}
                  {index < needsList.length - 1 && <br data-id="element-198" data-mp="1k5qvll404fsv" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-199" data-mp="30jquqtv0w1f2">Suddenly, you&apos;re facing the same questions again:</p>
            <ul className="space-y-2 pl-1" data-id="element-200" data-mp="2vi1ixbzz7jpx">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-[#520128] font-semibold" data-id="element-201" data-mp="1t4n5wno45bcy">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A24A] shrink-0 mt-3" aria-hidden="true" data-id="element-202" data-mp="1y2onf45w52mb" />
                  <span data-id="element-203" data-mp="226mnbh31ihgf">{question}</span>
                </li>)}
            </ul>
            <p className="text-[#8A6A2B] font-semibold" data-id="element-204" data-mp="274eh6gn8wdn9">
              You already know how important quality sewing education is.
            </p>
            <p data-id="element-205" data-mp="1efzcoo1qpdq6">But building every class from scratch can become a second job.</p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-206" data-mp="1jf3lhdkr59fz">
          <h3 className="text-xl font-bold text-[#520128] mb-8 text-center uppercase tracking-wide" data-id="element-207" data-mp="1jdslloufglu5">
            The Real Cost of Teaching Without a Complete System
          </h3>
          <div className="overflow-hidden rounded-xl border border-[#E6D5BF] bg-white shadow-sm shadow-[#520128]/5" data-id="element-208" data-mp="qpmu8j8vbylc">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-slate-700 text-sm leading-relaxed border-t first:border-t-0 border-[#E6D5BF]" data-id="element-209" data-mp="o600f63nxs89" data-mpi={index}>
                <X size={16} className="text-[#8E2C5C] shrink-0 mt-0.5" aria-hidden="true" data-id="element-210" data-mp="59gyb0ra14l7" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-[#E6D5BF]" data-id="element-211" data-mp="3rnqn3az4cemi">
            <p className="text-2xl font-bold tracking-tight text-[#520128]" data-id="element-212" data-mp="3monfbirr7uau">
              You don’t need more scattered sewing teaching resources.
            </p>
            <p className="text-xl mt-4 text-slate-700 font-semibold uppercase" data-id="element-213" data-mp="3io3lop6e38tg">
              You need a complete <span className="text-[#8E2C5C]" data-id="element-214" data-mp="1hk1bovucxwhe">professional system</span> that shows
              you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-215" data-mp="1llefz1hsxrm7">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#520128]" data-id="element-216" data-mp="17uhr9lqzxukf">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}